import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  Target, Eye, Lightbulb, ArrowRight, Users, Award, Zap, Globe,
  Brain, Building2, HeartPulse, Scale, Factory, TrendingUp
} from "lucide-react";

const expertise = [
  {
    icon: Brain,
    domain: "AI & Machine Learning",
    desc: "Deep expertise in LLMs, document intelligence, and predictive automation across enterprise environments."
  },
  {
    icon: Building2,
    domain: "Enterprise Architecture",
    desc: "Decades of experience integrating automation into complex legacy systems without disrupting operations."
  },
  {
    icon: HeartPulse,
    domain: "Healthcare & Compliance",
    desc: "Specialists in HIPAA-compliant workflows, clinical documentation, and regulatory process automation."
  },
  {
    icon: Scale,
    domain: "Legal & Contract Automation",
    desc: "End-to-end experience automating contract review, clause extraction, and legal document pipelines."
  },
  {
    icon: Factory,
    domain: "Manufacturing & Ops",
    desc: "Domain knowledge in shop-floor automation, supply chain optimization, and quality control workflows."
  },
  {
    icon: TrendingUp,
    domain: "ROI & Client Success",
    desc: "Dedicated focus on measurable outcomes — every engagement is tracked against concrete business metrics."
  },
];

const values = [
  {
    icon: Target,
    title: "Precision Over Promise",
    desc: "We solve specific problems, not abstract ones. Every engagement starts with mapping your exact pain points."
  },
  {
    icon: Eye,
    title: "Transparency First",
    desc: "No black boxes. We explain every model, every decision, and every metric so you stay in control."
  },
  {
    icon: Lightbulb,
    title: "Results in Weeks",
    desc: "We deploy fast. Our modular approach means you see working solutions within weeks, not quarters."
  },
  {
    icon: Zap,
    title: "Industry-Specific",
    desc: "We don't do generic AI. Every solution is built with deep understanding of your industry's nuances."
  },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="page-header-gradient border-b border-slate-100 py-16 md:py-24 relative overflow-hidden">
        <div className="hero-glow absolute inset-0" />
        <div className="dot-grid absolute inset-0 opacity-30" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Badge
            variant="outline"
            className="mb-4 px-4 py-1.5 text-blue-600 border-blue-200 bg-white/80 font-semibold backdrop-blur-sm"
          >
            About Us
          </Badge>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mb-4">
            We engineer the <span className="gradient-text">future of work</span>
          </h1>
          <p className="text-base md:text-lg text-slate-600 max-w-2xl leading-relaxed">
            AutoShiftAI was built on a simple observation: every industry has
            workflows that waste time, lose money, and frustrate people. AI can fix that.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 md:py-28" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm font-medium uppercase tracking-widest text-blue-600 mb-3">
                Our Mission
              </p>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-6">
                Replace inefficiency with precision
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-4">
                We've spent years studying how businesses actually work, not how consultants 
                think they work. The result? We've mapped 90+ specific manual processes across 
                17 industries that are bleeding time and money.
              </p>
              <p className="text-base text-slate-600 leading-relaxed mb-6">
                For each one, we've engineered an AI solution that doesn't just automate, 
                it optimizes. Our clients typically see 70% time savings within the first 
                month of deployment.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center p-5 bg-white rounded-xl border border-slate-100 shadow-card-base card-accent-blue">
                  <p className="font-heading text-3xl font-bold stat-value">17+</p>
                  <p className="text-xs text-slate-500 mt-1 font-medium">Industries</p>
                </div>
                <div className="text-center p-5 bg-white rounded-xl border border-slate-100 shadow-card-base card-accent-teal">
                  <p className="font-heading text-3xl font-bold stat-value">90+</p>
                  <p className="text-xs text-slate-500 mt-1 font-medium">Use Cases</p>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                alt="Team collaborating on AI solutions"
                className="rounded-xl shadow-lg w-full object-cover aspect-[4/3]"
                data-testid="about-team-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-28 section-gradient-bottom" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-medium uppercase tracking-widest text-blue-600 mb-3">
              Our Values
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">
              What drives every solution we build
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {values.map((val, idx) => {
              const Icon = val.icon;
              return (
                <Card
                  key={val.title}
                  className="bg-white border border-slate-200 card-lift card-accent-blue hover:border-blue-200 transition-all duration-300"
                  data-testid={`value-card-${idx}`}
                >
                  <CardContent className="p-8">
                    <div className="w-10 h-10 rounded-xl icon-bg-blue flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                      {val.title}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      {val.desc}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 md:py-28" data-testid="team-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-medium uppercase tracking-widest text-blue-600 mb-3">
              Our Expertise
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-3">
              A team built around domain mastery
            </h2>
            <p className="text-base text-slate-500 max-w-xl mx-auto">
              Our specialists bring deep, cross-industry knowledge to every engagement.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {expertise.map((item, idx) => {
              const Icon = item.icon;
              return (
                <Card
                  key={item.domain}
                  className="bg-white border border-slate-200 card-lift card-accent-teal hover:border-teal-200 transition-all duration-300"
                  data-testid={`team-card-${idx}`}
                >
                  <CardContent className="p-6">
                    <div className="w-10 h-10 rounded-xl icon-bg-teal flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-teal-600" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-heading text-sm font-semibold text-slate-900 mb-2">
                      {item.domain}
                    </h3>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      {item.desc}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 cta-gradient" data-testid="about-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to work with us?
          </h2>
          <p className="text-sm text-blue-100 max-w-xl mx-auto mb-8">
            Let's identify the workflows in your business that AI can transform.
          </p>
          <Link to="/contact">
            <Button
              data-testid="about-cta-button"
              className="bg-white text-blue-700 hover:bg-blue-50 rounded-lg px-8 py-3 h-auto font-bold shadow-lg hover:-translate-y-0.5 transition-all"
              size="lg"
            >
              Start a Conversation
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
